<template>
  <div v-if="device" class="flex flex-col h-full overflow-hidden">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedDevice?.name ? modifiedDevice.name : '-'"
      link-name="devices"
      :mode="mode"
      :is-modified="isModified"
      :is-edit-active="device.isFromManager || isAdmin || false"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer :layout="isHorizontalLayout ? 'vertical' : 'horizontal'" :minSize="300">
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <DeviceCreateEdit
            v-if="modifiedDevice"
            v-model="modifiedDevice"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout"
            :key="device?.id"
            :name-error-message="$vDev.name.required.$invalid ? $vDev.name.required.$message : ''"
            :code-error-message="
              $vDev.code.required.$invalid ? $vDev.code.required.$message : ''
            " />
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <DeviceCreateEdit
              v-if="modifiedDevice"
              v-model="modifiedDevice"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout"
              :key="device?.id"
              :name-error-message="$vDev.name.required.$invalid ? $vDev.name.required.$message : ''"
              :code-error-message="
                $vDev.code.required.$invalid ? $vDev.code.required.$message : ''
              " />
          </PerfectScrollbar>
        </template>
      </template>

      <template #detailsContent>
        <BaseTable
          v-if="filteredDeviceConfigurations"
          table-id="deviceConfigurations"
          :headers="tableHeaders"
          :list="filteredDeviceConfigurations"
          :is-add-new-hidden="mode != 'edit'"
          :is-search-hidden="true"
          clickable
          hide-grouping
          @deleteMultiselect="onDeleteMultiselect"
          @add-new="onAddNew"
          @refresh-data="refreshData"
          @table-item-clicked="onItemClick">
          <template #actions="{ item }">
            <div class="flex items-center justify-center gap-1">
              <ActionButton
                :isEnabled="
                  device.isFromManager && item.isFromManager && !item.deleted && mode == 'edit'
                "
                color="red"
                :icon="IconTrashOutline"
                @action="onDeleteRow(item as DeviceConfiguration)" />
            </div>
          </template>
        </BaseTable>

        <DeviceConfigurationCreateEdit
          v-if="isDeviceConfigurationModalActive && modifiedDeviceConfiguration"
          :device-configuration="modifiedDeviceConfiguration"
          :mode="modalMode == 'view' ? 'view' : 'edit'"
          :existing-configurations="modifiedDevice?.deviceConfigurations"
          :title="
            modalMode === 'add'
              ? $t('common.actions.create')
              : modalMode === 'edit'
                ? $t('common.actions.edit')
                : $t('common.actions.view')
          "
          @close="onDiscardDeviceConfigurationChanges"
          @save="onSaveDeviceConfiguration" />
        <BaseModalConfirm
          v-if="modalDeleteRow"
          @cancel="modalDeleteRow = false"
          @confirm="onDeleteSingle()" />
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="
        confirmModalActive = false;
        isBackMode = false;
      "
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';

import DeviceCreateEdit from '@/components/documents/DeviceCreateEdit.vue';
import DeviceConfigurationCreateEdit from '@/components/documents/DeviceConfigurationCreateEdit.vue';
import {
  addOrEditDevice,
  getDeviceById,
  getDeviceConfigurationsByDeviceId,
} from '@/services/api/documents';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';

import type { Device } from '@/types/Device';
import { eFunctionality, type DeviceConfiguration } from '@/types/DeviceConfiguration';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { sortData } from '@/utils/table';
import { useLookupsStore } from '@/stores/lookups';
import { helpers, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { deepCopy } from '@/utils/data';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';

const { getUsersLookup, getDocumentSeriesLookup, getDocumentTypesLookup } = useLookupsStore();

const { documentSeries, documentTypes, users, companySites } = storeToRefs(useLookupsStore());

const { isAdmin } = storeToRefs(useAuthStore());

const route = useRoute();
const router = useRouter();
const toastStore = useToastStore();

const device = ref<Device>();
const modifiedDevice = ref<Device>();
const isModified = ref<boolean>(false);
const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');
const confirmModalActive = ref(false);

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  {
    value: 'functionalityString',
    text: $t('device-configuration.functionality'),
    type: 'text',
    active: true,
  },
  { value: 'loadItemLines', text: $t('device.loadItemLines'), type: 'text', active: true },
  { value: 'device.name', text: $t('device.device'), type: 'text', active: true },
  {
    value: 'documentType.name',
    text: $t('device-configuration.documentType'),
    type: 'text',
    active: true,
  },
  {
    value: 'documentSeries.name',
    text: $t('device-configuration.documentSeries'),
    type: 'text',
    active: true,
  },
  {
    value: 'companySite.name',
    text: $t('device-configuration.companySite'),
    type: 'text',
    active: true,
  },
  { value: 'user.name', text: $t('device-configuration.defaultUser'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

async function loadData() {
  const resDevice = await getDeviceById(route.params.id as string);
  if (resDevice) {
    const resDevConf = await getDeviceConfigurationsByDeviceId(route.params.id as string);
    device.value = resDevice;
    device.value.deviceConfigurations = populateFunctionalityField(resDevConf);
  } else {
    device.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      name: '',
      remarks: '',
      deviceConfigurations: [],
    };
    mode.value = 'edit';
  }
}
onMounted(async () => {
  await getUsersLookup();
  await getDocumentSeriesLookup();
  await getDocumentTypesLookup();
  await loadData();
  if (device.value) modifiedDevice.value = deepCopy<Device>(device.value);
});

async function refreshData(data: TableFilterData) {
  if (!modifiedDevice.value?.deviceConfigurations) return;

  if (data.column !== undefined && data.order) {
    modifiedDevice.value.deviceConfigurations = sortData(
      modifiedDevice.value.deviceConfigurations,
      data.column,
      data.order,
      tableHeaders
    );
  }
}

function getFunctionalityString(functionality: number): string {
  switch (functionality) {
    case eFunctionality.CompetitorPriceCollection:
      return 'Competitor Price Collection';
    case eFunctionality.PriceCollection:
      return 'Price Collection';
    case eFunctionality.Other:
      return 'Other';
    case eFunctionality.Transactions:
      return 'Transactions';
    default:
      return 'Unknown';
  }
}

function populateFunctionalityField(
  deviceConfigurations: DeviceConfiguration[]
): DeviceConfiguration[] {
  return deviceConfigurations.map((deviceConfiguration) => ({
    ...deviceConfiguration,
    functionalityString:
      deviceConfiguration.functionality !== null
        ? getFunctionalityString(deviceConfiguration.functionality)
        : '',
  }));
}

watch(
  () => modifiedDevice.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(device.value);

    validationDeviceModel.value.name = newData?.name || '';
    validationDeviceModel.value.code = newData?.code || '';

    $vDev.value.$touch();
  },
  { deep: true }
);

const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!device.value?.createdDate) {
    router.push('/devices');
  }
  mode.value = 'view';
}

function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (device.value) modifiedDevice.value = deepCopy<Device>(device.value);
  if (!device.value?.createdDate || isBackMode.value) {
    router.push('/devices');
  }
}

async function onSaveChanges() {
  $vDev.value.$touch();
  if ($vDev.value.$invalid) return;

  mode.value = 'view';
  if (modifiedDevice.value) {
    modifiedDevice.value.isFromManager = true;
    let res = await addOrEditDevice(modifiedDevice.value);
    res ? toastStore.success({ text: 'Successfully saved' }) : toastStore.error({ text: res });
  }

  await loadData();
  if (device.value) modifiedDevice.value = deepCopy<Device>(device.value);
}

const deviceConfiguration = ref<DeviceConfiguration>();
const modifiedDeviceConfiguration = ref<DeviceConfiguration>();
const isDeviceConfigurationModalActive = ref(false);
const modalMode = ref<'add' | 'edit' | 'view'>('add');
const modalDeleteRow = ref(false);
const deleteRowItem = ref<DeviceConfiguration>();

const filteredDeviceConfigurations = computed(() => {
  return modifiedDevice.value?.deviceConfigurations?.filter((x) => x.recordMode != 3);
});

function initializeDeviceConfiguration(): DeviceConfiguration | undefined {
  if (device.value) {
    return {
      id: uuidv4(),
      code: '',
      name: '',
      functionality: eFunctionality.Other,
      functionalityString: eFunctionality[2],
      loadItemLines: 0,
      deviceID: device.value?.id,
      documentTypeID: '',
      documentSeriesID: '',
      companySiteID: '',
      isActive: true,
      isFromManager: true,
      deleted: false,
      device: {
        id: modifiedDevice.value?.id || device.value.id,
        code: modifiedDevice.value?.code || device.value.code,
        name: modifiedDevice.value?.name || device.value.name,
        remarks: modifiedDevice.value?.remarks || device.value.remarks,
        isActive: modifiedDevice.value?.isActive || device.value.isActive,
        updateUser: {
          id: device.value.updateUserId || '',
          userName: device.value.updateUser?.userName || '',
          name: device.value.updateUser?.name || '',
        },
        createUser: {
          id: device.value.createUserId || '',
          userName: device.value.createUser?.userName || '',
          name: device.value.createUser?.name || '',
        },
      },
      documentType: {
        id: '',
        code: '',
        name: '',
      },
      documentSeries: {
        id: '',
        code: '',
        name: '',
      },
      user: {
        id: '',
        userName: '',
        name: '',
      },
      recordMode: 1,
    };
  }
}

function onDeleteRow(item: DeviceConfiguration) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!modifiedDevice.value || !deleteRowItem.value) return;

  const idToDelete = deleteRowItem.value.id;

  if (!Array.isArray(modifiedDevice.value.deviceConfigurations))
    modifiedDevice.value.deviceConfigurations = [];

  const indexDevice = modifiedDevice.value.deviceConfigurations?.findIndex(
    (existingItem) => existingItem.id === idToDelete
  );

  if (indexDevice !== -1 && indexDevice !== undefined) {
    if (deleteRowItem.value.recordMode === RecordMode.Added)
      modifiedDevice.value.deviceConfigurations.splice(indexDevice, 1);
    else {
      deleteRowItem.value.recordMode = 3;
      modifiedDevice.value.deviceConfigurations[indexDevice] = deleteRowItem.value;
    }
  }
}

async function onDeleteMultiselect(items: DeviceConfiguration[]) {
  if (device.value?.isFromManager) {
    const itemsArray = Array.isArray(items) ? items : [items];
    const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
    if (itemsToDelete.length === 0) {
      toastStore.error({ text: 'Selected items cannot be deleted.' });
      return;
    }

    itemsToDelete.forEach(async (item) => {
      deleteRowItem.value = item;
      await onDeleteSingle();
    });
  }
}

function onAddNew() {
  $vDev.value.$touch();
  if ($vDev.value.$invalid) return;
  if (device.value?.isFromManager && mode.value == 'edit') {
    deviceConfiguration.value = initializeDeviceConfiguration();
    if (deviceConfiguration.value) {
      modifiedDeviceConfiguration.value = deepCopy<DeviceConfiguration>(deviceConfiguration.value);
    }
    isDeviceConfigurationModalActive.value = true;
    modalMode.value = 'add';
  }
}

function onItemClick(item: DeviceConfiguration) {
  deviceConfiguration.value = deepCopy<DeviceConfiguration>(item);
  if (deviceConfiguration.value) {
    if (deviceConfiguration.value.recordMode != RecordMode.Added)
      deviceConfiguration.value.recordMode = RecordMode.Modified;
  }
  modifiedDeviceConfiguration.value = deepCopy<DeviceConfiguration>(deviceConfiguration.value);
  isDeviceConfigurationModalActive.value = true;
  if (device.value?.isFromManager && item.isFromManager && mode.value == 'edit')
    modalMode.value = 'edit';
  else modalMode.value = 'view';
}

async function onSaveDeviceConfiguration(deviceConfig: DeviceConfiguration) {
  if (!modifiedDevice.value) return;
  if (!Array.isArray(modifiedDevice.value.deviceConfigurations))
    modifiedDevice.value.deviceConfigurations = [];

  const docSeries = documentSeries.value.find((item) => item.id === deviceConfig.documentSeriesID);
  if (docSeries) {
    deviceConfig.documentSeries = {
      id: docSeries.id,
      code: docSeries.code || '',
      name: docSeries.name || '',
    };
  }

  const docType = documentTypes.value.find((item) => item.id === deviceConfig.documentTypeID);
  if (docType) {
    deviceConfig.documentType = {
      id: docType.id,
      code: docType.code || '',
      name: docType.name || '',
    };
  }

  const user = users.value.find((item) => item.id === deviceConfig.defaultUserID);
  if (user) {
    deviceConfig.user = {
      id: user.id,
      userName: user.userName || '',
      name: user.name || '',
    };
  }

  const companySite = companySites.value.find((x) => x.id === deviceConfig.companySiteID);
  if (companySite) deviceConfig.companySite = companySite;

  const indexDev = modifiedDevice.value.deviceConfigurations.findIndex(
    (existingItem) => existingItem.id === modifiedDeviceConfiguration.value?.id
  );
  if (indexDev !== -1 && indexDev !== undefined)
    modifiedDevice.value.deviceConfigurations[indexDev] = deviceConfig;
  else modifiedDevice.value.deviceConfigurations.push(deviceConfig);

  deviceConfiguration.value = deepCopy<DeviceConfiguration>(deviceConfig);
  isDeviceConfigurationModalActive.value = false;
  modalMode.value = 'add';
}

function onDiscardDeviceConfigurationChanges() {
  if (deviceConfiguration.value) {
    modifiedDeviceConfiguration.value = deepCopy<DeviceConfiguration>(deviceConfiguration.value);
  }
  isDeviceConfigurationModalActive.value = false;
}

const validationDeviceModel = ref({
  name: modifiedDevice.value?.name || '',
  code: modifiedDevice.value?.code || '',
});

const rulesDev = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));
const $vDev = useVuelidate(rulesDev, validationDeviceModel, { $lazy: true });
</script>
